// Geolocation utilities for crew time clock

// Facility location - UPDATE THESE WITH YOUR ACTUAL COORDINATES
export const FACILITY_LOCATION = {
  latitude: 33.1234913472, // Replace with your facility's latitude
  longitude: -96.8848175, // Replace with your facility's longitude
};

// Maximum distance in meters to allow clock in/out (150m ≈ 500 feet)
export const MAX_CLOCK_DISTANCE_METERS = 1150;

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Get user's current position
 * Uses WiFi/IP positioning for desktop compatibility
 */
export function getCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      resolve,
      reject,
      {
        enableHighAccuracy: false, // Allow WiFi/IP positioning (not just GPS)
        timeout: 30000,            // 30 seconds to allow for slower methods
        maximumAge: 60000,         // Allow 1 minute cached positions
      }
    );
  });
}

/**
 * Check if user is within range of facility
 */
export async function isWithinFacilityRange(): Promise<{
  withinRange: boolean;
  distance: number;
  userLocation: { latitude: number; longitude: number };
}> {
  try {
    const position = await getCurrentPosition();
    const userLat = position.coords.latitude;
    const userLon = position.coords.longitude;

    const distance = calculateDistance(
      userLat,
      userLon,
      FACILITY_LOCATION.latitude,
      FACILITY_LOCATION.longitude
    );

    return {
      withinRange: distance <= MAX_CLOCK_DISTANCE_METERS,
      distance,
      userLocation: {
        latitude: userLat,
        longitude: userLon,
      },
    };
  } catch (error) {
    throw error;
  }
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} meters`;
  } else {
    return `${(meters / 1000).toFixed(1)} km`;
  }
}

/**
 * Convert meters to feet
 */
export function metersToFeet(meters: number): number {
  return meters * 3.28084;
}

/**
 * Format distance in feet
 */
export function formatDistanceFeet(meters: number): string {
  const feet = metersToFeet(meters);
  if (feet < 500) {
    return `${Math.round(feet)} feet`;
  } else {
    return `${(feet / 5280).toFixed(2)} miles`;
  }
}

